import { useEffect, useState } from 'react';

/** The app's mobile breakpoint. Keep in sync with the `max-width: 768px` media queries in CSS. */
export const SMALL_SCREEN_QUERY = '(max-width: 768px)';

const matches = (query: string) =>
    typeof window !== 'undefined' && window.matchMedia(query).matches;

/**
 * True while the media query matches, updating live on resize and rotation.
 * Prefer useIsSmallScreen; use this for a page-specific breakpoint that CSS
 * already uses, or for capabilities such as `(hover: none)`.
 */
export const useMediaQuery = (query: string): boolean => {
    const [isMatch, setIsMatch] = useState(() => matches(query));

    useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        const handleChange = (event: MediaQueryListEvent) => setIsMatch(event.matches);
        setIsMatch(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [query]);

    return isMatch;
};

/**
 * True below the mobile breakpoint, updating live on resize and rotation.
 * Use it when the markup itself must differ on phones (render or skip a
 * component, change what a tap does); plain styling belongs in CSS media queries.
 */
export const useIsSmallScreen = (): boolean => useMediaQuery(SMALL_SCREEN_QUERY);
